import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';
import { MAX_TAGS, MAX_TAG_LENGTH, splitTagDraft } from '../../common/tags';
/**
 * Upload dialog: click-to-choose, drag & drop, multi-file queue, drag-to-reorder
 * of the queue (the order becomes the set's display order), per-file and
 * overall progress (XHR upload events), retry on failure and cancel.
 *
 * One upload session is one image set: the set is created first (with the
 * optional title), then every file is sent with its `set_id` and `position`.
 * The files go to the API endpoint which stages them and answers with a
 * `pending` resource; the queue worker then transfers them to the image host.
 */
export default class WaterfallUploadModal extends Modal {
  queue = [];
  dragging = false;
  submitting = false;

  // Title of the set created on submit (empty = untitled).
  setTitle = '';
  // Freeform tags the user writes for the set (chips input). Normalised
  // client-side (trim / length / dedupe) and again server-side.
  tags = [];
  tagDraft = '';
  // The set created for this upload session, and whether it has been inserted
  // into the feed yet (done on the first successful file).
  set = null;
  setAdded = false;

  // Drag-to-reorder bookkeeping (keys, not indices: indices shift while
  // dragging).
  dragKey = null;
  dragOverKey = null;

  /**
   * In-flight `prepareItem` runs, awaited by submit() so an upload can never
   * start before the queued files have their card copies. They never reject.
   */
  preparing = [];
  keySeq = 0;
  className() {
    return 'WaterfallUploadModal Modal--large';
  }
  title() {
    return app.translator.trans('lcoy-waterfall.forum.upload_modal.title');
  }
  content() {
    const total = this.queue.length;
    const done = this.queue.filter(item => item.status === 'done' || item.status === 'error').length;
    const allDone = total > 0 && this.queue.every(item => item.status === 'done' || item.status === 'error');
    const hasErrors = this.queue.some(item => item.status === 'error');
    const overall = total > 0 ? Math.round(this.queue.reduce((sum, item) => sum + item.progress, 0) / total) : 0;
    return m("div", {
      className: "Modal-body WaterfallUploadModal-body"
    }, m("p", {
      className: "WaterfallUploadModal-description"
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.description')), m("div", {
      className: "WaterfallUploadModal-setTitle"
    }, m("label", {
      className: "WaterfallUploadModal-setTitleLabel",
      for: "waterfall-set-title"
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.set_title_label')), m("input", {
      id: "waterfall-set-title",
      type: "text",
      className: "FormControl",
      value: this.setTitle,
      maxlength: 200,
      disabled: this.submitting,
      placeholder: extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.set_title_placeholder')),
      oninput: e => {
        this.setTitle = e.target.value;
      }
    }), m("p", {
      className: "WaterfallUploadModal-setTitleHint"
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.set_title_hint'))), m("div", {
      className: "WaterfallUploadModal-tags"
    }, m("label", {
      className: "WaterfallUploadModal-setTitleLabel",
      for: "waterfall-set-tags"
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.tags_label')), m("div", {
      className: "WaterfallUploadModal-tagsField",
      onclick: () => this.focusTagInput()
    }, this.tags.map(tag => m("span", {
      className: "WaterfallUploadModal-tag",
      key: tag
    }, tag, m("button", {
      type: "button",
      className: "WaterfallUploadModal-tagRemove",
      "aria-label": extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.tag_remove', {
        tag
      })),
      disabled: this.submitting,
      onclick: () => {
        this.tags = this.tags.filter(existing => existing !== tag);
      }
    }, m("i", {
      className: "fas fa-times",
      "aria-hidden": "true"
    })))), this.tags.length < MAX_TAGS && m("input", {
      id: "waterfall-set-tags",
      type: "text",
      className: "WaterfallUploadModal-tagInput",
      value: this.tagDraft,
      disabled: this.submitting,
      placeholder: extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.tags_placeholder')),
      oninput: e => {
        const input = e.target;

        // A comma always separates tags — including the ones that
        // arrive by paste, which the old keydown-only handler turned
        // into a single over-long tag. A paste is a finished list, so
        // its last segment counts too; while typing, whatever follows
        // the last comma stays in the field.
        this.commitTagDraft(input.value, e.inputType.startsWith('insertFromPaste'));
      },
      onkeydown: e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.commitTagDraft(this.tagDraft, true);
        } else if (e.key === 'Backspace' && this.tagDraft === '') {
          this.tags = this.tags.slice(0, -1);
        }
      },
      onblur: () => this.commitTagDraft(this.tagDraft, true)
    })), m("p", {
      className: "WaterfallUploadModal-setTitleHint"
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.tags_hint', {
      count: MAX_TAGS,
      length: MAX_TAG_LENGTH
    }))), m("div", {
      className: classList('WaterfallUploadModal-dropzone', {
        'WaterfallUploadModal-dropzone--dragging': this.dragging
      }),
      ondragover: e => {
        e.preventDefault();

        // A queue item being reordered is not a new file drop.
        if (this.dragKey) {
          return;
        }
        this.dragging = true;
      },
      ondragleave: () => {
        this.dragging = false;
      },
      ondrop: e => {
        e.preventDefault();
        this.dragging = false;
        if (this.dragKey) {
          this.dragKey = null;
          this.dragOverKey = null;
          return;
        }
        this.addFiles(e.dataTransfer?.files);
      }
    }, m("i", {
      className: "fas fa-cloud-upload-alt",
      "aria-hidden": "true"
    }), m("p", null, app.translator.trans('lcoy-waterfall.forum.upload_modal.drop_here')), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.pickFiles()
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.choose_files')), m("input", {
      type: "file",
      className: "WaterfallUploadModal-fileInput",
      accept: "image/jpeg,image/png,image/gif,image/webp",
      multiple: true,
      onchange: e => {
        const input = e.target;
        this.addFiles(input.files);
        input.value = '';
      }
    })), this.queue.length > 0 && m("div", {
      className: "WaterfallUploadModal-queue",
      role: "list"
    }, this.queue.map(item => m("div", {
      key: item.key,
      className: classList('WaterfallUploadModal-item', `WaterfallUploadModal-item--${item.status}`, {
        'WaterfallUploadModal-item--dragging': this.dragKey === item.key
      }, {
        'WaterfallUploadModal-item--over': this.dragOverKey === item.key
      }),
      role: "listitem",
      draggable: item.status === 'ready' ? 'true' : 'false',
      ondragstart: e => {
        this.dragKey = item.key;
        this.dragOverKey = null;
        e.dataTransfer?.setData('text/plain', item.key);
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
        }
      },
      ondragover: e => {
        if (!this.dragKey || item.status !== 'ready' || this.dragKey === item.key) {
          return;
        }
        e.preventDefault();
        this.dragOverKey = item.key;
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }
      },
      ondragleave: () => {
        if (this.dragOverKey === item.key) {
          this.dragOverKey = null;
        }
      },
      ondrop: e => {
        e.preventDefault();
        e.stopPropagation();
        this.reorder(this.dragKey, item.key);
        this.dragKey = null;
        this.dragOverKey = null;
      },
      ondragend: () => {
        this.dragKey = null;
        this.dragOverKey = null;
      }
    }, item.status === 'ready' && m("div", {
      className: "WaterfallUploadModal-itemHandle",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-grip-vertical"
    })), m("div", {
      className: "WaterfallUploadModal-itemIcon"
    }, m("i", {
      className: "fas fa-file-image",
      "aria-hidden": "true"
    })), m("div", {
      className: "WaterfallUploadModal-itemBody"
    }, m("div", {
      className: "WaterfallUploadModal-itemName",
      title: item.file.name
    }, item.file.name), m("div", {
      className: "WaterfallUploadModal-itemMeta"
    }, item.status === 'ready' && item.width && item.height && m("span", null, item.width, "\xD7", item.height), item.status === 'uploading' && m("span", null, item.progress, "%"), item.status === 'done' && m("span", {
      className: "WaterfallUploadModal-itemDone"
    }, m("i", {
      className: "fas fa-check",
      "aria-hidden": "true"
    })), item.status === 'error' && m("span", {
      className: "WaterfallUploadModal-itemError"
    }, item.error)), (item.status === 'uploading' || item.status === 'done') && m("div", {
      className: "WaterfallUploadModal-progress"
    }, m("div", {
      className: "WaterfallUploadModal-progressBar",
      style: {
        width: `${item.progress}%`
      }
    }))), m("div", {
      className: "WaterfallUploadModal-itemActions"
    }, item.status === 'error' && m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-redo",
      "aria-label": extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.retry')),
      onclick: () => this.retryItem(item)
    }), item.status === 'uploading' ? m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-times",
      "aria-label": extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.cancel')),
      onclick: () => this.cancelItem(item)
    }) : m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-trash-alt",
      "aria-label": extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.remove')),
      onclick: () => {
        this.queue = this.queue.filter(existing => existing !== item);
        if (this.dragKey === item.key) {
          this.dragKey = null;
        }
        if (this.dragOverKey === item.key) {
          this.dragOverKey = null;
        }
      }
    }))))), total > 0 && m("div", {
      className: "WaterfallUploadModal-overall"
    }, m("span", {
      className: "WaterfallUploadModal-overallLabel"
    }, this.submitting ? app.translator.trans('lcoy-waterfall.forum.upload_modal.uploading', {
      done,
      total
    }) : app.translator.trans('lcoy-waterfall.forum.upload_modal.overall_progress')), m("div", {
      className: "WaterfallUploadModal-progress WaterfallUploadModal-progress--overall"
    }, m("div", {
      className: "WaterfallUploadModal-progressBar",
      style: {
        width: `${overall}%`
      }
    }))), allDone && m("div", {
      className: classList('WaterfallUploadModal-notice', {
        'WaterfallUploadModal-notice--error': hasErrors
      })
    }, hasErrors ? app.translator.trans('lcoy-waterfall.forum.upload_modal.failed_hint') : app.translator.trans('lcoy-waterfall.forum.upload_modal.finished')), m("div", {
      className: "WaterfallUploadModal-actions"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.submitting,
      disabled: !this.queue.some(item => item.status === 'ready'),
      onclick: () => this.submit()
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.submit')), m(Button, {
      className: "Button",
      onclick: () => this.hide()
    }, app.translator.trans('lcoy-waterfall.forum.upload_modal.close'))));
  }
  pickFiles() {
    this.element?.querySelector('.WaterfallUploadModal-fileInput')?.click();
  }

  /**
   * Commit the tags held in `raw`; see splitTagDraft for the splitting rules.
   */
  commitTagDraft(raw, commitTail) {
    if (raw === void 0) {
      raw = this.tagDraft;
    }
    if (commitTail === void 0) {
      commitTail = false;
    }
    const {
      tags,
      draft
    } = splitTagDraft(raw, this.tags, commitTail);
    this.tags = tags;
    this.tagDraft = draft;
  }
  focusTagInput() {
    this.element?.querySelector('.WaterfallUploadModal-tagInput')?.focus();
  }
  addFiles(fileList) {
    if (!fileList) {
      return;
    }
    for (const file of Array.from(fileList)) {
      if (!file.type.startsWith('image/')) {
        continue;
      }
      const item = {
        key: `wf-${this.keySeq++}`,
        file,
        title: file.name.replace(/\.[^.]+$/, ''),
        width: null,
        height: null,
        thumb: null,
        progress: 0,
        status: 'ready',
        error: null,
        xhr: null,
        cancelled: false
      };
      this.queue.push(item);
      this.preparing.push(this.prepareItem(item));
    }
  }

  /**
   * Move the dragged item to the drop target's position. The queue order is
   * the display order sent as each file's `position`.
   */
  reorder(fromKey, toKey) {
    if (!fromKey || fromKey === toKey) {
      return;
    }
    const from = this.queue.findIndex(item => item.key === fromKey);
    const to = this.queue.findIndex(item => item.key === toKey);
    if (from === -1 || to === -1) {
      return;
    }
    const [moved] = this.queue.splice(from, 1);
    this.queue.splice(to, 0, moved);
    m.redraw();
  }

  // Card thumbnails: the feed renders `thumb ?? src`, so a card-sized copy
  // keeps every grid visit from downloading the full-size original (a phone
  // photo is megabytes; the copy is tens of kilobytes). Files this small gain
  // nothing from one.
  static THUMB_MAX_EDGE = 800;
  static THUMB_SKIP_BYTES = 150_000;

  /**
   * Decode a queued file once and read everything the queue needs from the
   * decode: the intrinsic size for the label, and the card-sized copy. Runs in
   * the background while the user arranges the queue; submit() waits for it.
   *
   * Never rejects — a file that will not decode here will fail server-side
   * validation anyway, and an item without a copy is still uploadable.
   */
  async prepareItem(item) {
    try {
      const source = await this.decodeFile(item.file);
      item.width = source.width;
      item.height = source.height;
      item.thumb = await this.makeThumb(source, item.file);
      if ('close' in source) {
        source.close();
      }
    } catch {
      // A file that will not decode here will fail server-side validation
      // anyway; leaving the label off is enough.
    }
    m.redraw();
  }

  /**
   * Decode with EXIF orientation applied. `from-image` is what makes
   * createImageBitmap respect it; the <img> fallback inherits the same
   * behaviour from the CSS default `image-orientation: from-image`.
   */
  async decodeFile(file) {
    if ('createImageBitmap' in window) {
      try {
        const options = {};

        // 'from-image' postdates this TypeScript's lib.dom (whose union is
        // only "flipY" | "none"); every current engine accepts it, so poke
        // the value through without weakening the rest of the type.
        options.imageOrientation = 'from-image';
        return await createImageBitmap(file, options);
      } catch {
        // Engines that reject the options bag (or exotic files) fall through
        // to the <img> path below.
      }
    }
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('decode failed'));
        img.src = url;
      });
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Encode the card-sized copy. GIFs are skipped (a static frame would kill
   * the animation on the card), as are files with nothing to shrink. WebP is
   * asked for first and JPEG kept as the fallback; a browser that cannot
   * encode the requested type silently returns a PNG instead, so the blob's
   * actual type is what decides.
   */
  async makeThumb(source, file) {
    if (file.type === 'image/gif' || file.size < WaterfallUploadModal.THUMB_SKIP_BYTES) {
      return null;
    }
    const scale = Math.min(1, WaterfallUploadModal.THUMB_MAX_EDGE / Math.max(source.width, source.height));
    if (scale >= 1) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(source.width * scale);
    canvas.height = Math.round(source.height * scale);
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    // JPEG has no alpha channel, so a transparent source would come out on a
    // black background in the fallback encoder. White matches what the card
    // shows for a transparent image in a browser that *can* encode WebP.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    for (const type of ['image/webp', 'image/jpeg']) {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, type, 0.8));
      if (blob && blob.type === type) {
        return blob;
      }
    }
    return null;
  }

  /**
   * Create the set, then upload every queued file into it in queue order.
   */
  submit() {
    if (this.submitting) {
      return;
    }
    this.submitting = true;

    // An explicit submit re-arms items the user cancelled earlier: they are
    // back in the `ready` state, so leaving the flag set would make the button
    // do nothing for them. Cancels issued during this run still take effect,
    // because cancelItem() sets the flag while the chain is running.
    this.queue.forEach(item => {
      if (item.status === 'ready') {
        item.cancelled = false;
      }
    });

    // A tag typed into the field but never confirmed — no Enter, no blur yet —
    // still belongs to the set being published.
    this.commitTagDraft(this.tagDraft, true);
    const title = this.setTitle.replace(/\s+/gu, ' ').trim();

    // Decoding and re-encoding a batch of photos takes a moment, and the
    // upload button is live from the instant the first file lands in the
    // queue. Waiting for the in-flight work here is what makes the card copy
    // deterministic instead of a race the user can lose by clicking quickly.
    // These promises never reject (prepareItem swallows its own failures).
    Promise.all(this.preparing).then(() => app.store.createRecord('waterfall-sets').save({
      title: title === '' ? null : title,
      tags: this.tags.length > 0 ? this.tags : null
    })).then(set => {
      this.set = set;
      this.setAdded = false;
      return this.uploadQueue();
    }).then(() => this.finishSubmit()).catch(error => {
      this.submitting = false;
      const alert = error?.alert;
      if (alert) {
        app.alerts.show(alert, alert.content);
      } else {
        app.alerts.show({
          type: 'error'
        }, extractText(app.translator.trans('lcoy-waterfall.forum.upload_modal.set_failed')));
      }
      m.redraw();
    });
  }

  /**
   * Sequential upload keeps per-user concurrency within limits. Cancelled items
   * are skipped: the user explicitly stopped them, so re-uploading the same
   * item right after its abort would defeat the cancel.
   */
  uploadQueue() {
    const uploadNext = () => {
      const next = this.queue.find(item => item.status === 'ready' && !item.cancelled);
      if (!next) {
        return Promise.resolve();
      }
      return this.uploadItem(next).then(uploadNext).catch(uploadNext);
    };
    return uploadNext();
  }

  /**
   * Wrap up a submit run. If not a single file made it (all failed or were
   * cancelled), delete the freshly created empty set again instead of leaving
   * a phantom card in the feed.
   */
  finishSubmit() {
    this.submitting = false;
    const set = this.set;
    if (set && !this.queue.some(item => item.status === 'done')) {
      const state = this.attrs.waterfallState;
      this.set = null;
      this.setAdded = false;

      // Model.delete() removes the set from the store as part of the request;
      // only the feed array this page owns has to be told separately.
      set.delete().then(() => {
        state?.removeSet(set);
      }).catch(() => {}).then(() => m.redraw());
      return;
    }
    m.redraw();
  }
  retryItem(item) {
    item.cancelled = false;

    // Reuse the current set when it survived (some file succeeded); otherwise
    // a fresh set is created by submit().
    if (this.set && this.setAdded) {
      item.status = 'ready';
      this.uploadItem(item);
      return;
    }
    item.status = 'ready';
    this.submit();
  }
  uploadItem(item) {
    const set = this.set;
    if (!set) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      const body = new FormData();
      body.append('file', item.file);
      body.append('title', item.title);
      body.append('set_id', String(set.id()));
      body.append('position', String(this.queue.indexOf(item)));

      // The browser-encoded card copy travels in the same request; the queue
      // job forwards it to the image host after the original (see
      // ProcessImageUploadJob::transferThumbnail).
      if (item.thumb) {
        const ext = item.thumb.type.split('/')[1] || 'webp';
        body.append('thumb', item.thumb, `thumb.${ext}`);
      }
      const xhr = new XMLHttpRequest();
      item.xhr = xhr;
      item.status = 'uploading';
      item.progress = 0;
      item.error = null;
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          item.progress = Math.round(e.loaded / e.total * 100);
          m.redraw();
        }
      };
      xhr.onload = () => {
        item.xhr = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          item.status = 'done';
          item.progress = 100;
          const state = this.attrs.waterfallState;

          // Add the set to the feed once, on the first successful file; it is
          // then polled until the worker finishes the transfers.
          if (state && !this.setAdded) {
            this.setAdded = true;
            state.addUploadedSet(set);
          }
        } else {
          item.status = 'error';
          item.progress = 0;
          try {
            const payload = JSON.parse(xhr.responseText);
            const errors = payload?.errors?.[0]?.detail || payload?.errors?.[0]?.title;
            item.error = typeof errors === 'string' ? errors : `HTTP ${xhr.status}`;
          } catch {
            item.error = `HTTP ${xhr.status}`;
          }
        }
        m.redraw();
        resolve();
      };
      xhr.onerror = () => {
        item.xhr = null;
        item.status = 'error';
        item.progress = 0;
        item.error = extractText(app.translator.trans('core.lib.error.network_error_message'));
        m.redraw();
        resolve();
      };

      // Aborting must resolve the promise too, or the sequential upload
      // chain in submit() would hang forever after a cancel.
      xhr.onabort = () => {
        item.xhr = null;
        resolve();
      };
      xhr.open('POST', `${app.forum.attribute('apiUrl')}/waterfall-images`);
      xhr.setRequestHeader('X-CSRF-Token', app.session.csrfToken);
      xhr.send(body);
    });
  }
  cancelItem(item) {
    item.xhr?.abort();
    item.xhr = null;
    // Flag first, then reset: the abort resolution re-enters the sequential
    // upload chain, which must skip this item (see uploadQueue).
    item.cancelled = true;
    item.status = 'ready';
    item.progress = 0;
  }
}
flarum.reg.add('lcoy-waterfall', 'forum/components/WaterfallUploadModal', WaterfallUploadModal);